import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    TemplateRef,
    ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { miscFunctions } from 'mnm-webapp';

interface WrappedItem<T> {
    id: string;
    item: T;
}

@Component({
    selector: 'app-list-field',
    templateUrl: './list-field.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => ListFieldComponent),
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ListFieldComponent<T> implements ControlValueAccessor {
    @Input() public itemTemplate: TemplateRef<any>;

    // Used to determine which of the items are filled
    // correctly
    @Input() public validItems: (items: T[]) => T[];

    // At least one property in an item is filled
    // to be considered as valid.
    @Input() public useDefaultValidItems: boolean = false;

    // Whether to start with an empty item when the array is empty
    @Input() public startWithEmptyItem: boolean = false;

    @Input() public showAddButton: boolean = true;
    @Input()
    public set disabled(value: boolean) {
        this.isDisabled = !!value;
    }

    public wrappedItems: WrappedItem<T>[] = [];

    public isDisabled: boolean = false;

    public onChange: (items: T[]) => void;

    public constructor(private changeDetectorRef: ChangeDetectorRef) {}

    public emitChange = (): void => {
        if (!this.onChange) return;

        const items = this.validItems
            ? this.validItems(this.wrappedItems.map(x => x.item))
            : this.useDefaultValidItems
              ? this.wrappedItems
                    .map(x => x.item)

                    // Default valid items: filters out
                    // items that all of its properties are
                    // undefined or null
                    .filter(x => {
                        for (const prop in x) {
                            if (Object.prototype.hasOwnProperty.call(x, prop)) {
                                if (x[prop]) {
                                    return true;
                                }
                            }
                        }

                        return false;
                    })
              : this.wrappedItems.map(x => x.item);

        this.onChange(items);
    };

    public addItem(): void {
        const newItem = {} as any;

        // Auto-generate order number if the item has an 'order' property
        if (this.wrappedItems.length > 0) {
            const firstItem = this.wrappedItems[0].item;
            if (
                firstItem &&
                typeof firstItem === 'object' &&
                firstItem !== null &&
                'order' in firstItem
            ) {
                // Find the maximum order number in existing items
                const maxOrder = Math.max(
                    ...this.wrappedItems
                        .map(x => (x.item as any)?.order)
                        .filter(order => order != null && !isNaN(order))
                        .map(order => Number(order)),
                    0
                );
                newItem.order = maxOrder + 1;
            }
        }

        this.wrappedItems.push({
            id: miscFunctions.uuid(),
            item: newItem,
        } as any);
        this.emitChange();
    }

    public removeItem(wrappedItem: WrappedItem<T>): void {
        const idx = this.wrappedItems.findIndex(x => x.id === wrappedItem.id);
        if (idx !== -1) {
            this.wrappedItems.splice(idx, 1);
        }
        this.emitChange();
    }

    public writeValue(obj: T[]): void {
        this.wrappedItems =
            obj?.map(x => ({ id: miscFunctions.uuid(), item: x })) ?? [];

        // Auto-assign order numbers to existing items that don't have them
        if (this.wrappedItems.length > 0) {
            const firstItem = this.wrappedItems[0].item;
            if (
                firstItem &&
                typeof firstItem === 'object' &&
                firstItem !== null &&
                'order' in firstItem
            ) {
                let orderCounter = 1;
                this.wrappedItems.forEach(wrappedItem => {
                    const item = wrappedItem.item as any;
                    if (!item.order || item.order <= 0) {
                        item.order = orderCounter;
                    }
                    orderCounter = Math.max(orderCounter, item.order) + 1;
                });
            }
        }

        // Add an empty item only if startWithEmptyItem is true and array is empty
        // This is useful for required fields that need at least one item
        if (this.wrappedItems.length === 0 && this.startWithEmptyItem) {
            this.addItem();
        }

        this.changeDetectorRef.detectChanges();

        this.emitChange();
    }

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(): void {
        return undefined;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }
}
